import React, { useImperativeHandle, useRef } from "react";
import * as THREE from "three";
import { useFloorplannerStore } from '../../store/floorplannerStore';
import { WallType } from "../../types/floorplannerTypes";
import { FloorplannerStore } from "../../store/floorplannerStore";
import { ObservableMap } from "mobx";
import { projectPointOntoArc } from "../../utils/arcUtils";

interface AttachableSymbolProps {
  onAttachment: (attachPosition: [number, number]) => void;
  children: React.ReactNode;
  disableAttachment?: boolean;
  attachmentType: 'doorAttachments' | 'windowAttachments' | 'voidAttachments';
  attachmentId: string;
  store: FloorplannerStore;
}

const SNAP_THRESHOLD = 0.2; // Define the snap threshold for attachment
// Larger threshold for preferred wall - use a higher tolerance to keep symbols on the same wall during fast drags
const PREFERRED_WALL_THRESHOLD = 0.3;

/**
 * A component that allows an object to snap to walls. Must be a child of DraggableObject.
 * 
 * PERFORMANCE OPTIMIZATION:
 * This component implements "preferred wall caching" to avoid iterating through all walls
 * on every drag event. When dragging an attached symbol:
 * 1. The currently attached wall is checked FIRST with a slightly larger threshold
 * 2. Only if the symbol moves outside this threshold does it fall back to checking all walls
 * 
 * This dramatically speeds up drag operations, especially in floorplans with many walls.
 * 
 * @param onAttachment The function to call when the object snaps to a wall.
 * @param children The children to render.
 * @param disableAttachment Whether to disable attachment.
 * @param attachmentType The type of attachment.
 * @param attachmentId The ID of the attachment.
 * @param store The floorplanner store.
 * @returns The AttachableSymbol component.
 * 
 **/
const AttachableSymbol = React.forwardRef<unknown, AttachableSymbolProps>(
  ({ onAttachment, children, disableAttachment = false, attachmentType, attachmentId, store }, attachableRef) => {
    const groupRef = useRef<THREE.Group>(null);
    const { wallsMap } = useFloorplannerStore();

    // Cache the preferred wall ID for faster lookups during drag
    // This avoids iterating through all walls when the symbol is just sliding along the same wall
    const preferredWallIdRef = useRef<string | null>(null);

    /**
     * Calculates the closest point on a line segment to a given point.
     * @param point The external point.
     * @param start The start of the line segment.
     * @param end The end of the line segment.
     * @returns The closest point on the line segment.
     */
    const closestPointOnLineSegment = (
      point: THREE.Vector2,
      start: THREE.Vector2,
      end: THREE.Vector2
    ): THREE.Vector2 => {
      const line = end.clone().sub(start);
      const lengthSq = line.lengthSq();
      if (lengthSq === 0) return start.clone();

      const t = Math.max(0, Math.min(1, point.clone().sub(start).dot(line) / lengthSq));
      return start.clone().add(line.multiplyScalar(t));
    };

    /**
     * Check a single wall for proximity to the given point.
     * @param point The point to check.
     * @param wall The wall to check against.
     * @param threshold The distance threshold for snapping.
     * @returns Object with closest point and distance, or null if outside threshold.
     */
    const checkWallProximity = (
      point: THREE.Vector2,
      wall: WallType,
      threshold: number
    ): { point: THREE.Vector2, wall: WallType, distance: number } | null => {
      let candidatePoint: THREE.Vector2;

      if (wall.lineForm === "arc" && wall.controlPoint) {
        // Arc wall: use Bézier closest-point projection
        const cp = new THREE.Vector2(wall.controlPoint.x, wall.controlPoint.y);
        const result = projectPointOntoArc(
          point,
          new THREE.Vector2(wall.start.x, wall.start.y),
          cp,
          new THREE.Vector2(wall.end.x, wall.end.y),
        );
        candidatePoint = result.point;
      } else {
        const wallStart = new THREE.Vector2(wall.start.x, wall.start.y);
        const wallEnd = new THREE.Vector2(wall.end.x, wall.end.y);
        candidatePoint = closestPointOnLineSegment(point, wallStart, wallEnd);
      }

      const distance = point.distanceTo(candidatePoint);
      if (distance <= threshold) {
        return { point: candidatePoint, wall, distance };
      }
      return null;
    };

    /**
     * Finds the closest point on any wall to the given point within the SNAP_THRESHOLD.
     * OPTIMIZATION: Checks the preferred wall first before doing a full search.
     * @param point The point to check against walls.
     * @param walls The collection of walls to consider.
     * @param preferredWallId Optional wall ID to check first (caching optimization).
     * @returns The closest point if within threshold; otherwise, null.
     */
    const findClosestPointOnWalls = (
      point: THREE.Vector2,
      walls: ObservableMap<string, WallType>,
      preferredWallId?: string | null
    ): {
      point: THREE.Vector2,
      wall: WallType
    } | null => {
      // OPTIMIZATION: Check preferred wall first with slightly larger threshold
      // This avoids iterating through all walls when symbol is just sliding along the same wall
      if (preferredWallId) {
        const preferredWall = walls.get(preferredWallId);
        if (preferredWall) {
          const result = checkWallProximity(point, preferredWall, PREFERRED_WALL_THRESHOLD);
          if (result) {
            // Still on the preferred wall, return early without checking others
            return { point: result.point, wall: result.wall };
          }
        }
      }

      // Full search through all walls
      let minDistance = Infinity;
      let closestPoint: {
        point: THREE.Vector2,
        wall: WallType
      } | null = null;

      for (const [_, wall] of walls) {
        // Skip the preferred wall since we already checked it
        if (preferredWallId && wall.id === preferredWallId) continue;

        const result = checkWallProximity(point, wall, SNAP_THRESHOLD);
        if (result && result.distance < minDistance) {
          minDistance = result.distance;
          closestPoint = { point: result.point, wall: result.wall };
        }
      }

      return closestPoint;
    };

    useImperativeHandle(attachableRef, () => ({
      /**
       * Checks if the object is close enough to any wall to snap (attach) to it.
       * If so, invokes the attachToWall function.
       * @returns Object with snapped position and wall ID if attachment occurred, otherwise undefined.
       */
      checkForAttachment: (fromPosition: [number, number]): { position: [number, number], wallId: string } | undefined => {
        if (disableAttachment || !groupRef.current) return undefined;

        const objectPosition2D = new THREE.Vector2(fromPosition[0], fromPosition[1]);
        const closestPoint = findClosestPointOnWalls(objectPosition2D, wallsMap, preferredWallIdRef.current);

        if (closestPoint) {
          const snappedPosition: [number, number] = [closestPoint.point.x, closestPoint.point.y];
          // Update preferred wall cache
          preferredWallIdRef.current = closestPoint.wall.id;
          attachToWall(snappedPosition, closestPoint.wall);
          onAttachment(snappedPosition);
          return { position: snappedPosition, wallId: closestPoint.wall.id };
        }
        // No wall found, clear preferred wall cache
        preferredWallIdRef.current = null;
        return undefined;
      },
      /**
       * Finds the nearest wall to snap to without actually attaching.
       * Useful for deciding whether to detach or just slide along the current wall.
       * Uses preferred wall caching for performance.
       * @param preferredWallId Optional wall ID to check first (usually the currently attached wall).
       * @returns Object with snapped position and wall ID if a wall is nearby, otherwise undefined.
       */
      findNearestWall: (fromPosition: [number, number], preferredWallId?: string): { position: [number, number], wallId: string } | undefined => {
        if (disableAttachment) return undefined;

        const objectPosition2D = new THREE.Vector2(fromPosition[0], fromPosition[1]);
        // Use provided preferredWallId or fall back to cached value
        const wallIdToCheck = preferredWallId || preferredWallIdRef.current;
        const closestPoint = findClosestPointOnWalls(objectPosition2D, wallsMap, wallIdToCheck);

        if (closestPoint) {
          // Update preferred wall cache
          preferredWallIdRef.current = closestPoint.wall.id;
          return {
            position: [closestPoint.point.x, closestPoint.point.y],
            wallId: closestPoint.wall.id
          };
        }
        // No wall found, clear preferred wall cache
        preferredWallIdRef.current = null;
        return undefined;
      },
      /**
       * Sets the preferred wall ID for faster lookups.
       * Call this when the symbol is attached to a wall.
       */
      setPreferredWall: (wallId: string | null) => {
        preferredWallIdRef.current = wallId;
      },
      /**
       * Clears the preferred wall cache.
       * Call this when the symbol is detached from all walls.
       */
      clearPreferredWall: () => {
        preferredWallIdRef.current = null;
      },
    }));

    const attachToWall = (attachPosition: [number, number], wall: WallType) => {
      store.attachSymbolToWall(attachmentId, wall.id, attachPosition);
    };

    return <group ref={groupRef}>{children}</group>;
  }
);

export default AttachableSymbol;
